/* eslint-disable */
// Events hub — two tabs: "Upcoming" (merged feed of community events + events
// you're invited to, with a list/calendar toggle) and "Hosted by me" (the
// management list of your hosted events). Replaces the old separate
// EventsPage + HostedEventsPage (/hosted-events now redirects here).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CalendarDays, CalendarPlus, Check, Clock, List, MapPin, Plus, Tag, X,
} from "lucide-react";
import { eventsApi } from "./api";
import { hostedEventsApi } from "../hosted-events/api";
import { useAuth } from "../auth/AuthContext";
import MonthCalendar from "./MonthCalendar";
import { venueLine } from "./location";
import { fmtTime12, toYmd } from "./dateTimePickers";
import "./EventsPage.css";
import "../hosted-events/HostedEvents.css";

function dateBlock(dstr) {
  const d = new Date(dstr + "T00:00:00");
  return {
    month: d.toLocaleString("en-IN", { month: "short" }).toUpperCase(),
    day: d.getDate(),
  };
}

function monthHeader(dstr) {
  const d = new Date(dstr + "T00:00:00");
  const label = d.toLocaleString("en-IN", { month: "long" });
  return d.getFullYear() === new Date().getFullYear()
    ? label
    : `${label} ${d.getFullYear()}`;
}

function KindBadge({ item }) {
  if (item.kind === "community")
    return <span className="ev-badge ev-badge-community">Community</span>;
  if (item.kind === "hosting")
    return <span className="ev-badge ev-badge-hosting">Hosting</span>;
  return (
    <span className="ev-badges">
      <span className="ev-badge ev-badge-invited">Invited</span>
      {item.rsvp === "pending" ? (
        <span className="ev-badge ev-badge-rsvp">RSVP needed</span>
      ) : item.rsvp === "yes" ? (
        <span className="ev-badge ev-badge-going"><Check size={11} /> Going</span>
      ) : (
        <span className="ev-badge ev-badge-declined"><X size={11} /> Declined</span>
      )}
    </span>
  );
}

// One feed row — shared by the Upcoming list and the calendar's day panel.
function FeedRow({ item, onClick }) {
  const { month, day } = dateBlock(item.event_date);
  const venue = venueLine(item.location, item.venue);
  return (
    <div className="he-card" role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}>
      <div className="he-date-block">
        <span className="he-month">{month}</span>
        <span className="he-day">{day}</span>
      </div>
      <div className="he-card-info">
        <div className="he-card-titlerow">
          <h3 className="he-card-title">{item.title}</h3>
          <KindBadge item={item} />
        </div>
        {venue && (
          <div className="he-meta"><MapPin size={12} /> <span>{venue}</span></div>
        )}
        {item.start_time && (
          <div className="he-meta">
            <Clock size={12} />
            <span>
              {fmtTime12(item.start_time)}
              {item.end_time ? ` – ${fmtTime12(item.end_time)}` : ""}
            </span>
          </div>
        )}
        {item.kind === "community" && item.category && (
          <div className="he-meta"><Tag size={12} /> <span>{item.category}</span></div>
        )}
        {item.kind === "invited" && item.host && (
          <div className="he-meta he-muted">Hosted by {item.host.name}</div>
        )}
        {item.kind === "hosting" && item.counts && (
          <div className="he-counts">
            <span className="he-c-yes">{item.counts.yes} going</span>
            <span className="he-c-pending">{item.counts.pending} pending</span>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: ["Draft", "he-pill-draft"],
    invited: ["Invited", "he-pill-invited"],
    cancelled: ["Cancelled", "he-pill-cancelled"],
  };
  const [label, cls] = map[status] || [status, ""];
  return <span className={`he-pill ${cls}`}>{label}</span>;
}

function HostedCard({ ev, onClick }) {
  const { month, day } = dateBlock(ev.event_date);
  const venue = venueLine(ev.location, ev.venue);
  return (
    <div className="he-card" role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}>
      <div className="he-date-block">
        <span className="he-month">{month}</span>
        <span className="he-day">{day}</span>
      </div>
      <div className="he-card-info">
        <div className="he-card-titlerow">
          <h3 className="he-card-title">{ev.title}</h3>
          <StatusPill status={ev.status} />
        </div>
        {venue && (
          <div className="he-meta"><MapPin size={12} /> <span>{venue}</span></div>
        )}
        {ev.start_time && (
          <div className="he-meta"><Clock size={12} /> <span>{fmtTime12(ev.start_time)}</span></div>
        )}
        <div className="he-counts">
          <span className="he-c-yes">{ev.counts.yes} going</span>
          <span className="he-c-no">{ev.counts.no} no</span>
          <span className="he-c-pending">{ev.counts.pending} pending</span>
        </div>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "hosted" ? "hosted" : "upcoming";
  const setTab = (t) =>
    setSearchParams(t === "hosted" ? { tab: "hosted" } : {}, { replace: true });

  // ── Upcoming: default feed (today → +90d) ─────────────────────────────────
  const [view, setView] = useState("list"); // "list" | "calendar"
  const [feedItems, setFeedItems] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState("");

  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    setFeedError("");
    try {
      const data = await eventsApi.feed();
      setFeedItems(data.items ?? []);
    } catch (e) {
      setFeedError(e.message || "Could not load events");
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // The Upcoming list is "what can I attend": community + invited only.
  // Hosting dates still show on the calendar (complete picture).
  const upcoming = useMemo(
    () => feedItems.filter((it) => it.kind === "community" || it.kind === "invited"),
    [feedItems]
  );

  // ── Calendar: feed for the visible month (all three kinds) ────────────────
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calItems, setCalItems] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState("");
  const [selectedDay, setSelectedDay] = useState("");

  useEffect(() => {
    if (view !== "calendar") return;
    const from = toYmd(calMonth);
    const to = toYmd(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0));
    let alive = true;
    setCalLoading(true);
    setCalError("");
    eventsApi.feed(from, to)
      .then((data) => { if (alive) setCalItems(data.items ?? []); })
      .catch((e) => { if (alive) setCalError(e.message || "Could not load calendar"); })
      .finally(() => { if (alive) setCalLoading(false); });
    return () => { alive = false; };
  }, [view, calMonth]);

  const dayItems = useMemo(
    () => (selectedDay ? calItems.filter((it) => it.event_date === selectedDay) : []),
    [calItems, selectedDay]
  );

  // ── Hosted by me ──────────────────────────────────────────────────────────
  const [hosting, setHosting] = useState([]);
  const [hostedLoading, setHostedLoading] = useState(true);
  const [hostedError, setHostedError] = useState("");

  const loadHosted = useCallback(async () => {
    setHostedLoading(true);
    setHostedError("");
    try {
      const data = await hostedEventsApi.list();
      setHosting(data.hosting ?? []);
    } catch (e) {
      setHostedError(e.message || "Could not load your events");
    } finally {
      setHostedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "hosted") loadHosted();
  }, [tab, loadHosted]);

  const openItem = (item) =>
    navigate(item.kind === "community" ? `/events/${item.id}` : `/hosted-events/${item.id}`);

  // Upcoming list with month grouping headers
  const renderUpcomingList = () => {
    if (feedLoading) return <p className="ev-loading">Loading…</p>;
    if (feedError) return <p className="ev-error">{feedError}</p>;
    if (upcoming.length === 0)
      return <p className="ev-empty">No upcoming events. Check back soon!</p>;
    const rows = [];
    let lastMonth = "";
    for (const item of upcoming) {
      const mh = monthHeader(item.event_date);
      if (mh !== lastMonth) {
        lastMonth = mh;
        rows.push(<h2 key={`h-${mh}`} className="ev-month-header">{mh}</h2>);
      }
      rows.push(
        <FeedRow key={`${item.kind}-${item.id}`} item={item} onClick={() => openItem(item)} />
      );
    }
    return <div className="ev-list">{rows}</div>;
  };

  const renderCalendar = () => (
    <div className="ev-cal-wrap">
      <MonthCalendar
        month={calMonth}
        onMonthChange={(m) => { setCalMonth(m); setSelectedDay(""); }}
        items={calItems}
        selectedDate={selectedDay}
        onSelectDay={setSelectedDay}
      />
      {calLoading ? (
        <p className="ev-loading">Loading…</p>
      ) : calError ? (
        <p className="ev-error">{calError}</p>
      ) : selectedDay ? (
        <div className="ev-day-panel">
          <h2 className="ev-month-header">
            {new Date(selectedDay + "T00:00:00").toLocaleDateString("en-IN", {
              weekday: "long", day: "numeric", month: "long",
            })}
          </h2>
          {dayItems.length === 0 ? (
            <p className="ev-empty">Nothing on this day.</p>
          ) : (
            <div className="ev-list">
              {dayItems.map((item) => (
                <FeedRow key={`${item.kind}-${item.id}`} item={item} onClick={() => openItem(item)} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="ev-empty">Tap a day to see its events.</p>
      )}
    </div>
  );

  return (
    <div className="ev-page">
      <div className="ev-header">
        <h1 className="ev-heading">Events</h1>
        {tab === "upcoming" && isAdmin && (
          <button className="ev-create-btn" onClick={() => navigate("/events/new")}>
            <Plus size={16} /> Add Event
          </button>
        )}
        {tab === "hosted" && (
          <button className="ev-create-btn" onClick={() => navigate("/hosted-events/new")}>
            <CalendarPlus size={16} /> Host an event
          </button>
        )}
      </div>

      <div className="he-tabs ev-tabs-row">
        <div className="ev-tabs">
          <button className={`he-tab ${tab === "upcoming" ? "active" : ""}`}
            onClick={() => setTab("upcoming")}>
            Upcoming
          </button>
          <button className={`he-tab ${tab === "hosted" ? "active" : ""}`}
            onClick={() => setTab("hosted")}>
            Hosted by me
          </button>
        </div>
        {tab === "upcoming" && (
          <div className="ev-view-toggle" role="group" aria-label="View">
            <button
              className={`ev-view-btn ${view === "list" ? "active" : ""}`}
              aria-label="List view"
              onClick={() => setView("list")}
            >
              <List size={15} />
            </button>
            <button
              className={`ev-view-btn ${view === "calendar" ? "active" : ""}`}
              aria-label="Calendar view"
              onClick={() => setView("calendar")}
            >
              <CalendarDays size={15} />
            </button>
          </div>
        )}
      </div>

      {tab === "upcoming" ? (
        view === "list" ? renderUpcomingList() : renderCalendar()
      ) : hostedLoading ? (
        <p className="ev-loading">Loading…</p>
      ) : hostedError ? (
        <p className="ev-error">{hostedError}</p>
      ) : hosting.length === 0 ? (
        <p className="ev-empty">
          You're not hosting any events yet. Tap "Host an event" to start.
        </p>
      ) : (
        <div className="ev-list">
          {hosting.map((ev) => (
            <HostedCard key={ev.id} ev={ev}
              onClick={() => navigate(`/hosted-events/${ev.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}
